import React, { useEffect, useMemo, useState } from 'react'
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Outlet, useParams } from "react-router-dom";
import {
  useLazyGetMeetingInfoQuery,
} from '../../app/services/conference.service'
import { FixMeLater } from '../../types'
import { ConferenceClientMessageType, ConferenceClientOptions } from '../../types/conference.types'
import { useAppDispatch, useAppSelector, useIsElectron } from '../../app/hooks'
import Options from '../../components/navigation-bar/options/Options'
import AccountSettings from '../account-settings/AccountSettings'
import Avatar, { AvatarSize } from '../../components/avatar/Avatar'
import colors from '../../utils/colors'
import DropdownAlert from '../../components/dropdown-alert/DropdownAlert'
import { VIDEO_CALL } from '../../locales/locales'
import { conferenceActions } from '../../app/slices/conference.slice'
import ConferenceInviteModal from './video-conference-invite/ConferenceInvite'
import CustomIcon from '../../components/icon/CustomIcon'
import { icons } from '../../utils/icons'
import CustomText from '../../components/text/CustomText'
import { useRingtone } from 'utils/hooks/useRingtone';

const clientOptions: ConferenceClientOptions = {
  enableDevices: false,
  hideShareLink: true,
  displayGdpr: true,
  dontShowJoinPanel: false,
  useCamera: true,
  useMicrophone: true,
  useTargetOrigin: true,
  postMessageAsString: false,
  locale: 'tr',
  hideChangeLanguage: false,
  pollEnabled: true,
  showInvitationPanel: false,
  desktopApplicationEnabled: false,
  showFullscreenButton: false,
  meetingSettingsPanel: {
    show: true,
    muteAllCameras: true,
    muteAllMicrophones: true,
    togglePublicDraw: true,
    togglePublicMeeting: true,
    toggleGuestMode: true,
    togglePrivateChat: false,
    broadcastMeeting: true,
    pollEnabled: true,
  },
  controlBar: {
    record: true,
    share: true,
    remote: true,
    draw: true,
    layout: true,
    hand: true,
    audio: true,
    video: true,
    switch: true,
    participants: true,
    chat: false,
    settings: true,
    leave: true
  },
}

export default function MeetingMock() {
  const { t } = useTranslation();
  const isElectron = useIsElectron()
  const dispatch = useAppDispatch()
  const authUser = useAppSelector((state) => state.auth.user)
  const users = useAppSelector((state) => state.users.data)
  const responses = useAppSelector((state) => state.conference.responses)
  const [visible, setVisible] = useState(false);
  const [visibleAvatar, setVisibleAvatar] = useState(false);
  const [visibleRoomInfo, setVisibleRoomInfo] = useState(false)
  const [visibleAccountSettings, setVisibleAccountSettings] = useState(false)
  const {meetingId = "" } = useParams();
  const [getMeetingInfo] = useLazyGetMeetingInfoQuery();
  const [meetingUrl, setMeetingUrl] = useState<string | undefined>();
  const [meetingTitle, setMeetingTitle] = useState<string>('');
  const [isHost, setIsHost] = useState<boolean>(false);


  const notificationLanguage =
    useAppSelector((state) => state.auth?.user?.notificationLanguage) || 'tr'

  useEffect(() => {
    fetchMeetingUrl()
  }, [])

  sessionStorage.setItem('isInMeeting', 'true');

  const fetchMeetingUrl = async () => {
    try {
      const { data } = (await getMeetingInfo({ meetingId: meetingId })) as FixMeLater
      clientOptions.locale = notificationLanguage;
      const qt = btoa(JSON.stringify(clientOptions));
      setMeetingUrl(`${data.data.url}?qt=${qt}`);
      setMeetingTitle(data.data.title);
      setIsHost(data.data.isCreator);
    }
    catch (e) {
      console.log(e)
    }
  }

  const listenMeetingMessages = (event: MessageEvent) => {
    if(!event.data.type)
      return;

    switch (event.data.type) {
      case ConferenceClientMessageType.JoinedMeeting:
        console.log('Joined meeting');
        break;
      case ConferenceClientMessageType.LeftMeeting:
        console.log('Left meeting');
        window.close();
        break;
      case ConferenceClientMessageType.MeetingEnded:
        console.log('Meeting ended');
        window.close();
        break;
      case ConferenceClientMessageType.UserRoleChange:
        console.log('User role changed in meeting');
        setIsHost(event.data.isHost)
        break;
    }
  }

  useEffect(() => {
    if(!responses || responses.length === 0)
      return;

    const response = responses.find((response: any) => response.meetingId === meetingId);
    if(response && !response.isAccepted) {
      DropdownAlert.ref.show({
        isError: true,
        message: t(VIDEO_CALL.VIDEO_CALL_REJECTED, {
          from: response.from
        }),
      })
      dispatch(conferenceActions.removeResponse({ meetingId }))

    }
  }, [responses])

  useEffect(() => {
    const win = window as any
    win.addEventListener('message', listenMeetingMessages)

    return () => {
      win.removeEventListener('message', listenMeetingMessages)
    }
  }, [])

  const onToggleConversationPanel = () => {
    setVisible((prev: boolean) => !prev);
  };

  const onToggleRoomInfo = () => {
    setVisibleRoomInfo((prev: boolean) => !prev)
  }

  const onFullscreenClick = () => {
    const doc = document as any

    if (
      doc['fullscreenElement'] ||
      doc['mozFullScreenElement'] ||
      doc['webkitFullscreenElement'] ||
      doc['msFullscreenElement']
    ) {
      exitFullscreen().call(document)
//      document.exitFullscreen()
    } else {
//      document.documentElement.requestFullscreen()
      requestFullscreen().call(document.documentElement)
    }
  }
  const requestFullscreen = ()=> {
    const docEl = document.documentElement as any;
    return docEl['requestFullscreen'] || docEl['mozRequestFullScreen'] || docEl['webkitRequestFullScreen'] || docEl['msRequestFullscreen'];
  }

  const exitFullscreen = () =>{
    const doc = document as any

    return doc['exitFullscreen'] || doc['mozCancelFullScreen'] || doc['webkitExitFullscreen'] || doc['msExitFullscreen'];
  }

  const Content = useMemo(() => {
    return <Outlet context={{ onToggleConversationPanel }} />;
  }, [onToggleConversationPanel]);

  const onOpenAccountSettings = () => {
    setVisibleAccountSettings(true)
    onToggleVisible()
  }


  const onCloseAccountSettings = () => {
    setVisibleAccountSettings(false)
  }

  const onToggleVisible = () => {
    setVisibleAvatar(!visibleAvatar)
  }

  return (
    <Wrapper>
      <TopBar>
        <TopLeft>
          {visibleAvatar && (
            <Options
              userOnly={true}
              onOpenAccountSettings={onOpenAccountSettings}
              onHide={onToggleVisible}
            />
          )}
          {visibleAccountSettings && <AccountSettings onClose={onCloseAccountSettings} />}

          <Avatar
            onClick={onToggleVisible}
            type="circle"
            size={AvatarSize.large}
            source={authUser?.avatar}
            text={authUser?.fullname}
            status={users[authUser._id]?.status}
            color={colors.orange}
          />
          <Title>{meetingTitle}</Title>
        </TopLeft>

        <TopCenter></TopCenter>

        <TopRight>
          {isHost && (
            <TopbarButtonContainer onClick={onToggleRoomInfo}>
              <CustomIcon source={icons.userInvite} iconPreset={'large'} />
              <CustomText typography="body9" textAlign="center" cursor="pointer">
                {t(VIDEO_CALL.INVITE_PARTICIPANT)}
              </CustomText>
            </TopbarButtonContainer>
          )}
          <TopbarButtonContainer onClick={onToggleConversationPanel}>
            <CustomIcon source={icons.chat} iconPreset={'large'} />
            <CustomText typography="body9" textAlign="center" cursor="pointer">
              {t(VIDEO_CALL.CHAT)}
            </CustomText>
          </TopbarButtonContainer>
          {!isElectron &&<TopbarButtonContainer onClick={onFullscreenClick}>
            <CustomIcon source={icons.fullscreen} iconPreset={'large'} />
            <CustomText typography="body9" textAlign="center" cursor="pointer">
              {t(VIDEO_CALL.FULLSCREEN)}
            </CustomText>
          </TopbarButtonContainer>}
        </TopRight>
      </TopBar>

      <ContentBody>
        {visibleRoomInfo && <ConferenceInviteModal onClose={onToggleRoomInfo} />}
        <ConferenceArea>
          {meetingUrl && (
            <ConferenceFrame
              src={meetingUrl}
              title="Conference"
              allow="camera; microphone; fullscreen; display-capture"
              allowFullScreen
            />
          )}
          <ConferenceOverlay />
        </ConferenceArea>

        {visible && <ChatPanel>{Content}</ChatPanel>}
      </ContentBody>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f3f2f1;
  overflow: hidden;
`;

const TopBar = styled.div`
  height: 60px;
  min-height: 60px;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  padding: 0 18px;
  background: #ffffff;
  border-bottom: 1px solid #e1dfdd;
  box-sizing: border-box;
`;

const TopLeft = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
    gap: 12px;
`;

const TopCenter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 18px;
`;

const TopRight = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 18px;
`

const Title = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #201f1e;
`;



const ContentBody = styled.div`
  flex: 1;
  display: flex;
  width: 100%;
  min-height: 0;
  overflow: hidden;
`;

const ConferenceArea = styled.div`
  flex: 1;
  min-width: 0;
  position: relative;
  background: #000;
`;

const ConferenceFrame = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  display: block;
  background: #000;
`;

const ConferenceOverlay = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: rgba(0, 0, 0, 0.08);
`;

const ChatPanel = styled.div`
  width: 380px;
  min-width: 380px;
  max-width: 380px;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-left: 1px solid #e1dfdd;
  box-sizing: border-box;
  flex-shrink: 0;
`

const TopbarButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 10px;
  border-radius: 4px;
  cursor: pointer;
  
  &:hover {
    background-color: ${(props) => colors.gray20};
  }
`
